import { ButtonLink, FormInput, FormPhone, TextWithLink } from '@/components'
import { Categories } from '@/enums'
import {
  useFormContext,
  useNextLoading,
  useOnBackClick,
  useOnNextClick,
} from '@/providers'
import { paths } from '@/routes/paths'
import {
  deleteLocalStorageItems,
  makeUrlWithLanguage,
  mixpanelIdentify,
  mixpanelTrack,
  openInNewTab,
  setLocalStorageItem,
  useLanguage,
} from '@/utils'
import { useCallback } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useOutletContext, useSearchParams } from 'react-router-dom'
import { useAssessmentValues } from '../helpers'

const useMessages = () => {
  const { t } = useTranslation()

  return {
    name: t('name'),
    firstName: t('firstName'),
    lastName: t('surname'),
    phoneNumber: t('phone'),
    email: t('email'),
    acceptTerms: t('acceptTerms'),
  }
}

const makeTermsUrl = makeUrlWithLanguage(paths.terms)
const makePolicyUrl = makeUrlWithLanguage(paths.policy)

interface MarketingContext {
  marketingPid: string
  marketingMid: string
}

interface MutationResponse {
  accessToken?: string
}

interface CreateAccountValues {
  name: string
  phone: string
  email: string
}

interface AssessmentValues {
  createAccount: CreateAccountValues
  [key: string]: any
}

interface SubmitBody {
  name: string
  surname: string
  phone: string
  email: string
  partner: string | null
  marketingPid: string
  marketingMid: string
  assessmentValues?: any
}

interface CreateAccountStepProps {
  goTo: (stepName: string) => void
  goToStepName: string
  onBack: () => void
  recommendationProductId?: string
  categoryId: keyof typeof Categories
  mutation: {
    isLoading: boolean
    mutate: (
      body: any,
      options: { onSuccess: (data: MutationResponse) => void },
    ) => void
  }
  transformSubmitValues?: (values: any) => any
}

export const CreateAccountStep = ({
  goTo,
  goToStepName,
  onBack,
  recommendationProductId,
  categoryId,
  mutation,
  transformSubmitValues,
}: CreateAccountStepProps) => {
  const language = useLanguage()
  const messages = useMessages()
  const {
    values: { createAccount: values, ...assessmentValues },
  } = useAssessmentValues() as { values: AssessmentValues }
  const { customValidate } = useFormContext()
  const localStorageKeys = [
    `${categoryId}-values`,
    `${categoryId}-current-step`,
    `${categoryId}-confirmation-id`,
  ]
  const [searchParams] = useSearchParams()

  // Marketing specific params
  const { marketingPid, marketingMid } = useOutletContext<MarketingContext>()
  const partner = searchParams.get('partner')

  const save = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        customValidate({
          onSuccess: () => {
            const [name, ...surname] = values.name.split(' ')
            const formattedSurname = surname.join(' ') || '_'

            const body: SubmitBody = {
              name,
              surname: formattedSurname,
              phone: values.phone,
              email: values.email,
              partner,
              marketingPid,
              marketingMid,
            }

            if (transformSubmitValues) {
              body.assessmentValues = transformSubmitValues(assessmentValues)
            }

            mutation.mutate(body, {
              onSuccess: ({ accessToken }: MutationResponse) => {
                deleteLocalStorageItems(localStorageKeys)
                if (accessToken) {
                  setLocalStorageItem('access-token', accessToken)
                  mixpanelIdentify(values.email)
                  mixpanelTrack('account_created')
                } else {
                  mixpanelTrack('account_created_failed_reco', {
                    name,
                    surname: formattedSurname,
                    email: values.email,
                  })
                }
                resolve()
              },
            })
          },
          onFail: reject,
        })
      }),
    [values],
  )

  const goBack = useCallback(async (): Promise<void> => {
    if (recommendationProductId) {
      onBack()
    } else {
      goTo(goToStepName)
    }
  }, [recommendationProductId])

  useOnBackClick(goBack)

  useOnNextClick(save)

  useNextLoading(() => mutation.isLoading, [mutation.isLoading])

  return (
    <>
      <FormInput label={messages.name} name="name" />
      <FormPhone label={messages.phoneNumber} name="phone" />
      <FormInput label={messages.email} name="email" />
      <TextWithLink>
        <Trans
          components={[
            <ButtonLink
              onClick={() => openInNewTab(makeTermsUrl(language))}
              size="small"
            />,
            <ButtonLink
              onClick={() => openInNewTab(makePolicyUrl(language))}
              size="small"
            />,
          ]}
          i18nKey={messages.acceptTerms}
        />
      </TextWithLink>
    </>
  )
}
